import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectIsAuthenticated } from '../features/user/userSlice';
import {
  resetSignUp,
  selectNumberOfLicenses,
  selectStep,
  selectSessionId,
  selectMentor,
  selectThinkers,
  selectTrackGroup,
  selectAmounts,
  setStep,
  setTrackGroup,
} from '../features/signUp/signUpSlice';
import { store, useAppSelector } from '../stores/AppStore';
import {
  useGetPriceConfigQuery,
  useGetAllPricesQuery,
  useUpdateSignUpSessionMutation,
} from '../services/apiSlice';

import MentorDetails from '../components/SignUp/MentorDetails';
import Payment from '../components/SignUp/Payment/Payment';
import SubscriptionConfirm from '../components/SignUp/SubscriptionConfirm';
import ThinkerDetails from '../components/SignUp/ThinkerDetails';
import TrackRecommendation from '../components/SignUp/TrackRecommendation';
import SignUpQuestionnaire from '../components/SignUp/Questionnaire';
import toast from 'react-hot-toast';
import About from './About';
import { TrackGroup } from '../types';

const SignUp = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const step = useAppSelector(selectStep);
  const numberOfLicenses = useAppSelector(selectNumberOfLicenses);
  const sessionId = useAppSelector(selectSessionId);
  const mentor = useAppSelector(selectMentor);
  const thinkers = useAppSelector(selectThinkers);
  const trackGroup = useAppSelector(selectTrackGroup);
  const amounts = useAppSelector(selectAmounts);
  const [previousIndex, setPreviousIndex] = useState(1);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const skipAbout = searchParameters.get('skipAbout');
  const program = searchParameters.get('program');
  const recovered = searchParameters.get('recovered');
  const [updateSignUpSession] = useUpdateSignUpSessionMutation();

  // Prefetch price data immediately when signup page loads
  // This ensures pricing is available when users reach the payment step
  useGetPriceConfigQuery(undefined, {
    // Refetch if data is older than 5 minutes to balance freshness with performance
    refetchOnMountOrArgChange: 300, // 5 minutes in seconds
  });

  useGetAllPricesQuery(undefined, {
    // Refetch if data is older than 5 minutes to balance freshness with performance
    refetchOnMountOrArgChange: 300, // 5 minutes in seconds
  });

  const handleNextStep = async () => {
    const nextStep = step + 1;
    store.dispatch(setStep(nextStep));

    // Update session with current form data if we have a sessionId
    if (sessionId) {
      try {
        const formData = {
          mentor,
          thinkers,
          numberOfLicenses,
          trackGroup,
          amounts,
        };

        await updateSignUpSession({
          sessionId,
          currentStep: nextStep,
          formData,
        }).unwrap();

        console.log('Updated sign-up session for step:', nextStep);
      } catch (error) {
        console.error('Failed to update sign-up session:', error);
        // Don't block user progress if session update fails
      }
    }
  };

  const handlePreviousStep = () => {
    store.dispatch(setStep(step - 1));
  };

  useEffect(() => {
    setTimeout(() => {
      setPreviousIndex(step);
    }, 500);
  }, [step]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/login');
      return;
    }

    // Don't reset state if coming from recovery
    if (recovered) {
      setSearchParameters('');
      return;
    }

    // Defensive check: If we're at the final step (subscription confirmation),
    // it means the sign up was likely completed but state wasn't cleared properly.
    // Clear it now to prevent issues.
    const currentStep = store.getState().signUp.step;
    const finalStep = 4 + numberOfLicenses * 2;
    if (currentStep === finalStep) {
      console.log('Clearing stale sign-up state from completed flow');
      store.dispatch(resetSignUp(0));
    }

    const firstStep = typeof skipAbout === 'string' ? 1 : 0;
    store.dispatch(resetSignUp(firstStep));
    if (program === 'ACT') {
      store.dispatch(setTrackGroup(TrackGroup.ACTSATPrep));
    }
    setSearchParameters('');
  }, []);

  const renderStep = () => {
    const thinkerDetailSteps = numberOfLicenses * 2; // Each thinker detail step followed by a track recommendation step

    if (step === 0) {
      return <About onGetStarted={handleNextStep} />;
    }
    if (step === 1) {
      return (
        <Payment
          onPreviousStep={handlePreviousStep}
          onNextStep={(paymentType) => {
            if (paymentType === 'trial') {
              toast.success(
                `Your 7-day trial has been activated! Now let's set up your account.`,
              );
            } else if (paymentType === 'discounted') {
              toast.success(
                `Subscription activated with your discount! Now let's set up your account.`,
              );
            } else {
              toast.success(
                `Successful payment! Now let's set up your account.`,
              );
            }
            handleNextStep();
          }}
        />
      );
    }
    if (step === 2) {
      return (
        <MentorDetails
          onPreviousStep={handlePreviousStep}
          onNextStep={handleNextStep}
        />
      );
    }
    if (step >= 3 && step < 3 + thinkerDetailSteps) {
      const thinkerSetupStep = step - 3;
      return thinkerSetupStep % 2 === 0 ? (
        <ThinkerDetails
          onNextStep={handleNextStep}
          onPreviousStep={handlePreviousStep}
          licenseIndex={Math.floor(thinkerSetupStep / 2)}
        />
      ) : (
        <TrackRecommendation
          onNextStep={handleNextStep}
          onPreviousStep={handlePreviousStep}
          licenseIndex={Math.floor(thinkerSetupStep / 2)}
        />
      );
    }
    if (step === 3 + thinkerDetailSteps) {
      return (
        <SignUpQuestionnaire
          onPreviousStep={handlePreviousStep}
          onNextStep={handleNextStep}
        />
      );
    }
    if (step === 4 + thinkerDetailSteps) {
      return <SubscriptionConfirm />;
    }
  };

  return (
    <div
      className={`
        ${
          step > 1 ? 'mt-8 md:mt-0' : ''
        } flex flex-col mx-auto w-full h-full md:w-[90%] items-center p-4
        ${
          previousIndex < step
            ? 'animate-slideInFromRight'
            : previousIndex > step
            ? 'animate-slideInFromLeft'
            : ''
        }
      `}
    >
      {renderStep()}
    </div>
  );
};

export default SignUp;
